import React, { useEffect } from "react";
import PropTypes from "prop-types";

import { Generic, Field, Control, Label, Select, Icon, Help } from "rbx";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useQuery } from "react-apollo";
import { Link } from "react-router-dom";

import { ALL_LOCATIONS_QUERY } from "../../graphql/Locations/Locations.graphql";
import { ourToast as toast } from "../../utils";

const LocationSelect = ({
  value,
  label,
  size,
  valueKey,
  name,
  onChange,
  icon,
  required,
  readOnly,
  where,
  disabled,
  addons,
  showHelp,
  needBeChanged,
  displayFn = Location =>
    `${Location.LocationID ? `${Location.LocationID} - ` : ""}${
      Location.LocationName ? `${Location.LocationName} - ` : ""
    }${
      Location.City && Location.State
        ? `${Location.City}, ${Location.State}`
        : ""
    }${Location.TimeZone ? ` - ${Location.TimeZone.CodeId}` : ""}`
}) => {
  const { data, loading, error } = useQuery(ALL_LOCATIONS_QUERY, {
    variables: {
      where,
      orderByMulti: ["LocationID_ASC"]
    },
    fetchPolicy: "cache-and-network"
  });
  if (error) {
    toast.error("Error getting Locations.");
  }
  const selectedLocation = data?.Locations?.find(
    l => l.RecId === parseInt(value)
  );
  const helpText = selectedLocation
    ? `${selectedLocation.Address1 ? `${selectedLocation.Address1}, ` : ""}${
        selectedLocation.Address2 ? `${selectedLocation.Address2}, ` : " "
      }${
        selectedLocation.City && selectedLocation.State
          ? `${selectedLocation.City}, ${selectedLocation.State} ${
              selectedLocation.Zip ? selectedLocation.Zip : ""
            }`
          : ""
      }`
    : " ";

  useEffect(() => {
    if (
      data?.Locations &&
      value &&
      selectedLocation &&
      needBeChanged &&
      parseInt(needBeChanged.RecId, 10) !== parseInt(selectedLocation.RecId, 10)
    ) {
      if (name === "PickupLocationRecIdRef") {
        onChange({
          target: { value: selectedLocation, name: "PickupLocation" }
        });
      } else if (name === "DeliveryLocationRecIdRef") {
        onChange({
          target: { value: selectedLocation, name: "DeliveryLocation" }
        });
      }
    }
  }, [value, data, selectedLocation, needBeChanged, name, onChange]);

  return (
    <Field>
      <Label size={size}>{label}</Label>
      <Field kind={addons ? "addons" : ""}>
        <Control expanded iconLeft size={size}>
          <Select.Container
            fullwidth
            color={error ? "danger" : "secondary"}
            size={size}
            state={loading ? "loading" : null}
          >
            {data && data.Locations ? (
              <Select
                disabled={disabled || readOnly || loading}
                name={name}
                required={required}
                value={value}
                onChange={onChange}
              >
                <Select.Option value={0}>{label}</Select.Option>
                {!loading &&
                  data.Locations.map((Location, i) => (
                    <Select.Option
                      key={`location-select-${i}`}
                      value={Location[valueKey]}
                    >
                      {displayFn(Location)}
                    </Select.Option>
                  ))}
              </Select>
            ) : null}
          </Select.Container>
          {icon && (
            <Icon align="left" size="small">
              <FontAwesomeIcon icon={icon} />
            </Icon>
          )}
        </Control>
        {addons && <Control>{addons}</Control>}
      </Field>
      {showHelp && (
        <Help as={Label} style={{ marginLeft: "2.5rem" }}>
          <Generic
            as={Link}
            tabIndex="-1"
            textColor="link"
            to={`/dispatch/locations/${selectedLocation?.RecId}/details`}
          >
            {helpText}
          </Generic>
        </Help>
      )}
    </Field>
  );
};
LocationSelect.propTypes = {
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  name: PropTypes.string,
  onChange: PropTypes.func,
  label: PropTypes.string,
  size: PropTypes.string,
  valueKey: PropTypes.string,
  icon: PropTypes.string,
  required: PropTypes.bool,
  readOnly: PropTypes.bool,
  where: PropTypes.object,
  disabled: PropTypes.bool,
  addons: PropTypes.node,
  showHelp: PropTypes.bool,
  displayFn: PropTypes.func,
  needBeChanged: PropTypes.object
};
LocationSelect.defaultProps = {
  value: 0,
  valueKey: "RecId",
  where: {},
  icon: "globe",
  showHelp: true,
  needBeChanged: null
};
export default LocationSelect;
